import { AxiosError, AxiosRequestConfig } from 'axios';
import {
  ActionCreatorWithPayload,
  AsyncThunkPayloadCreator,
  createAsyncThunk,
} from '@reduxjs/toolkit';

import HTTPRequest from 'src/axios.config';
import { Dispatch, RootState } from 'src/store';
import notification from 'src/store/ui/notification';
import { Callback, OnError, OnSuccess } from 'src/interfaces/general';
import app from 'src/store/ui/app';

export type RejectedError = {
  message: string;
  code: number;
};

export type ThunkConfig = {
  dispatch: Dispatch;
  state: RootState;
  rejectValue: AxiosError;
};

export interface ThunkOption<Returned> extends AxiosRequestConfig {
  extraAction?: ActionCreatorWithPayload<any>;
  onSuccess?: OnSuccess<Returned>;
  onError?: OnError;
  shouldNotify?: boolean;
}

export type Creator<Returned, Arguments> = (
  data: Returned,
) => AsyncThunkPayloadCreator<any, Arguments, ThunkConfig>;

export type GetArgument<Returned, Arguments> = (
  arg1?: string | Creator<Returned, Arguments> | ThunkOption<Returned>,
  arg2?: Creator<Returned, Arguments> | ThunkOption<Returned>,
  arg3?: ThunkOption<Returned>,
) => {
  typePrefix?: string;
  creator?: Creator<Returned, Arguments>;
  option?: ThunkOption<Returned>;
};

export default class Thunk extends HTTPRequest {
  prefix: string = '';
  config?: AxiosRequestConfig;

  constructor(prefix?: string, config?: AxiosRequestConfig) {
    super(config);

    this.prefix = prefix || '';
    this.config = config;
  }

  private static getArguments<Returned, Arguments>(
    ...args: Parameters<GetArgument<Returned, Arguments>>
  ) {
    const [arg1, arg2, arg3] = args;

    let typePrefix: string | undefined;
    let creator: Creator<Returned, Arguments> | undefined;
    let option: ThunkOption<Returned> | undefined;

    if (typeof arg1 === 'string') {
      typePrefix = arg1;

      if (typeof arg2 === 'function') {
        creator = arg2;
        option = arg3;
      } else {
        option = arg2;
      }
    } else if (typeof arg1 === 'function') {
      creator = arg1;
      option = arg2 as ThunkOption<Returned>;
    } else {
      option = arg1;
    }

    return { typePrefix, creator, option };
  }

  public fetchResources<Returned, Arguments>(
    typePrefix: string,
    creator: AsyncThunkPayloadCreator<any, Arguments, ThunkConfig>,
  ) {
    return createAsyncThunk<Returned, Arguments, ThunkConfig>(
      `fetchResources_${typePrefix}`,
      async (args, thunkAPI) => {
        try {
          thunkAPI.dispatch(app.updateLoading(true));
          const res = await creator(args, thunkAPI);

          thunkAPI.dispatch(app.updateLoading(false));
          return res;
        } catch (err) {
          thunkAPI.dispatch(app.updateLoading(false));

          const error = err as unknown as AxiosError;
          return thunkAPI.rejectWithValue(error);
        }
      },
    );
  }

  public fetch<Returned, Arguments>(
    ...args: Parameters<GetArgument<Returned, Arguments>>
  ) {
    const { typePrefix, creator, option } = Thunk.getArguments<
      Returned,
      Arguments
    >(...args);
    const apiUrl = typePrefix || this.prefix;
    const {
      extraAction,
      onSuccess: optionSuccess,
      onError: optionError,
      ...rest
    } = option || {};

    return createAsyncThunk<
      Returned,
      Callback<Returned, Arguments>,
      ThunkConfig
    >(`get_${apiUrl}`, async (args, thunkAPI) => {
      const {
        onSuccess: argsSuccess,
        onError: argsError,
        ...params
      } = args || {};
      const onSuccess = argsSuccess || optionSuccess;
      const onError = argsError || optionError;

      try {
        extraAction && thunkAPI.dispatch(extraAction(true));
        const res = await this.get<Returned>(apiUrl, {
          params,
          ...this.config,
          ...rest,
        });

        await creator?.(res.data)?.(args, thunkAPI);
        await onSuccess?.({
          data: res.data,
          dispatch: thunkAPI.dispatch,
          args,
        });

        extraAction && thunkAPI.dispatch(extraAction(false));
        return res.data as Returned;
      } catch (err) {
        const error = err as unknown as AxiosError;

        onError?.({
          dispatch: thunkAPI.dispatch,
          error: error.response?.data,
          args,
        });
        extraAction && thunkAPI.dispatch(extraAction(false));
        return thunkAPI.rejectWithValue(error);
      }
    });
  }

  public fetchOne<Returned, Arguments = {}>(
    ...args: Parameters<GetArgument<Returned, Arguments>>
  ) {
    const { typePrefix, creator, option } = Thunk.getArguments<
      Returned,
      Arguments
    >(...args);
    const apiUrl = typePrefix || this.prefix;
    const {
      extraAction,
      onSuccess: optionSuccess,
      onError: optionError,
      ...rest
    } = option || {};

    return createAsyncThunk<
      Returned,
      Callback<Returned, Arguments> & { id: string | number },
      ThunkConfig
    >(`get_one_${apiUrl}`, async (args, thunkAPI) => {
      const {
        onSuccess: argsSuccess,
        onError: argsError,
        id,
        ...params
      } = args || {};
      const onSuccess = argsSuccess || optionSuccess;
      const onError = argsError || optionError;

      try {
        extraAction && thunkAPI.dispatch(extraAction(true));
        const res = await this.get<Returned>(`${apiUrl}/${id}`, {
          params,
          ...this.config,
          ...rest,
        });

        await creator?.(res.data)?.(args, thunkAPI);
        await onSuccess?.({
          data: res.data,
          dispatch: thunkAPI.dispatch,
          args,
        });

        extraAction && thunkAPI.dispatch(extraAction(false));
        return res.data as Returned;
      } catch (err) {
        const error = err as unknown as AxiosError;

        extraAction && thunkAPI.dispatch(extraAction(false));
        onError?.({
          dispatch: thunkAPI.dispatch,
          error: error.response?.data,
          args,
        });
        return thunkAPI.rejectWithValue(error);
      }
    });
  }

  public create<Returned, Arguments>(
    ...args: Parameters<GetArgument<Returned, Arguments>>
  ) {
    const { typePrefix, creator, option } = Thunk.getArguments<
      Returned,
      Arguments
    >(...args);
    const apiUrl = typePrefix || this.prefix;
    const {
      extraAction,
      onSuccess: optionSuccess,
      onError: optionError,
      shouldNotify = true,
      ...rest
    } = option || {};

    return createAsyncThunk<
      Returned,
      Callback<Returned, Arguments>,
      ThunkConfig
    >(`post_${apiUrl}`, async (args, thunkAPI) => {
      const {
        onSuccess: argsSuccess,
        onError: argsError,
        ...payload
      } = args || {};
      const onSuccess = argsSuccess || optionSuccess;
      const onError = argsError || optionError;

      try {
        extraAction && thunkAPI.dispatch(extraAction(true));
        const res = await this.post<Returned>(apiUrl, payload, {
          ...this.config,
          ...rest,
        });

        await creator?.(res.data)?.(payload as Arguments, thunkAPI);
        await onSuccess?.({
          data: res.data,
          dispatch: thunkAPI.dispatch,
          args,
        });

        extraAction && thunkAPI.dispatch(extraAction(false));
        return res.data as Returned;
      } catch (err) {
        const error = err as unknown as AxiosError;

        shouldNotify &&
          thunkAPI.dispatch(
            notification.on({
              type: 'error',
              message: error.response?.data?.message || 'An error occurred',
            }),
          );

        extraAction && thunkAPI.dispatch(extraAction(false));
        onError?.({
          dispatch: thunkAPI.dispatch,
          error: error.response?.data,
          args,
        });
        return thunkAPI.rejectWithValue(error);
      }
    });
  }

  public update<Returned, Arguments>(
    ...args: Parameters<GetArgument<Returned, Arguments>>
  ) {
    const { typePrefix, creator, option } = Thunk.getArguments<
      Returned,
      Arguments
    >(...args);
    const apiUrl = typePrefix || this.prefix;
    const {
      extraAction,
      onSuccess: optionSuccess,
      onError: optionError,
      shouldNotify = true,
      ...rest
    } = option || {};

    return createAsyncThunk<
      Returned,
      Callback<Returned, Arguments> & { id?: string | number },
      ThunkConfig
    >(`put_${apiUrl}`, async (args, thunkAPI) => {
      const {
        onSuccess: argsSuccess,
        onError: argsError,
        id,
        ...payload
      } = args || {};
      const onSuccess = argsSuccess || optionSuccess;
      const onError = argsError || optionError;

      try {
        extraAction && thunkAPI.dispatch(extraAction(true));
        const url = id ? `${apiUrl}/${id}` : apiUrl;

        const res = await this.put<Returned>(url, payload, {
          ...this.config,
          ...rest,
        });

        await creator?.(res.data)?.(payload as Arguments, thunkAPI);
        await onSuccess?.({
          data: res.data,
          dispatch: thunkAPI.dispatch,
          args,
        });

        extraAction && thunkAPI.dispatch(extraAction(false));
        return res.data as Returned;
      } catch (err) {
        const error = err as unknown as AxiosError;

        shouldNotify &&
          thunkAPI.dispatch(
            notification.on({
              type: 'error',
              message: error.response?.data?.message || 'An error occurred',
            }),
          );

        extraAction && thunkAPI.dispatch(extraAction(false));
        onError?.({
          dispatch: thunkAPI.dispatch,
          error: error.response?.data,
          args,
        });
        return thunkAPI.rejectWithValue(error);
      }
    });
  }

  public remove<Returned, Arguments = {}>(
    ...args: Parameters<GetArgument<Returned, Arguments>>
  ) {
    const { typePrefix, creator, option } = Thunk.getArguments<
      Returned,
      Arguments
    >(...args);
    const apiUrl = typePrefix || this.prefix;
    const {
      extraAction,
      onSuccess: optionSuccess,
      onError: optionError,
      shouldNotify = true,
      ...rest
    } = option || {};

    return createAsyncThunk<
      Returned,
      Callback<Returned, Arguments> & { id?: string | number },
      ThunkConfig
    >(`delete_${apiUrl}`, async (args, thunkAPI) => {
      const {
        onSuccess: argsSuccess,
        onError: argsError,
        id,
        ...payload
      } = args || {};
      const onSuccess = argsSuccess || optionSuccess;
      const onError = argsError || optionError;

      try {
        extraAction && thunkAPI.dispatch(extraAction(true));
        const url = id ? `${apiUrl}/${id}` : apiUrl;

        const res = await this.delete<Returned>(url, {
          ...this.config,
          ...rest,
        });

        await creator?.(res.data)?.(payload as Arguments, thunkAPI);
        await onSuccess?.({
          data: res.data,
          dispatch: thunkAPI.dispatch,
          args,
        });

        extraAction && thunkAPI.dispatch(extraAction(false));
        return res.data as Returned;
      } catch (err) {
        const error = err as unknown as AxiosError;

        shouldNotify &&
          thunkAPI.dispatch(
            notification.on({
              type: 'error',
              message: error.response?.data?.message || 'An error occurred',
            }),
          );

        extraAction && thunkAPI.dispatch(extraAction(false));
        onError?.({
          dispatch: thunkAPI.dispatch,
          error: error.response?.data,
          args,
        });
        return thunkAPI.rejectWithValue(error);
      }
    });
  }
}
